import { useState, useEffect } from 'react';
import { Grid, Button, Typography } from '@mui/material';
import DialogUI from './DialogUI';

/**
 * @typedef {Object} ConfirmDialogButton
 * @property {string} label
 * @property {'text'|'outlined'|'contained'} [variant]
 * @property {string} [color] - Hex szín (#RRGGBB vagy #RGB), vagy MUI színkulcs ('primary' | 'secondary' | stb.).
 * @property {(e:any)=> (void|Promise<void>)} [onClick]
 * @property {boolean} [disabled]
 * @property {boolean} [autoFocus]
 * @property {boolean} [cancel]
 */

// Hex helpers
const HEX_RE = /^#(?:[A-Fa-f0-9]{3}){1,2}$/;
/** @param {string|undefined} v */
const isHexColor = (v) => typeof v === 'string' && HEX_RE.test(v);
/** @param {string} hex */
const hexToRgb = (hex) => {
  let h = hex.replace('#', '');
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  const num = parseInt(h, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};
/** @param {string} hex */
const getContrastColor = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  const L = 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  return L > 0.179 ? '#000000' : '#ffffff';
};
/** @param {string} hex @param {number} amount (0..1) */
const darkenHex = (hex, amount = 0.1) => {
  const { r, g, b } = hexToRgb(hex);
  const d = (c) => Math.max(0, Math.min(255, Math.round(c * (1 - amount))));
  const toHex = (c) => c.toString(16).padStart(2, '0');
  return `#${toHex(d(r))}${toHex(d(g))}${toHex(d(b))}`;
};
/** @param {string} hex @param {number} a (0..1) */
const rgba = (hex, a) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};
/** @param {string} hex @param {'text'|'outlined'|'contained'} [variant] */
const getHexColorSx = (hex, variant = 'contained') => {
  if (variant === 'outlined') {
    return {
      color: hex,
      borderColor: hex,
      '&:hover': { backgroundColor: rgba(hex, 0.08), borderColor: darkenHex(hex, 0.1) },
    };
  }
  if (variant === 'text') {
    return {
      color: hex,
      '&:hover': { backgroundColor: rgba(hex, 0.08) },
    };
  }
  // contained (default)
  return {
    backgroundColor: hex,
    color: getContrastColor(hex),
    '&:hover': { backgroundColor: darkenHex(hex, 0.1) },
  };
};

/**
 * MUI alapú megerősítő párbeszédablak. A gombokat egy tömb írja le, gridben (xs=12, md=6, lg=4) elrendezve.
 * A “Mégse” gomb mindig az utolsó és bezárja a dialógust.
 *
 * @param {Object} props
 * @param {boolean} [props.open]          - Láthatóság (elsődleges).
 * @param {(e?: any) => void} [props.onClose] - Bezárás callback.
 * @param {string|import('react').ReactNode} [props.title]   - Cím.
 * @param {string|import('react').ReactNode} [props.message] - Üzenet.
 * @param {ConfirmDialogButton[]} [props.buttons]            - Művelet gombok listája (a “Mégse” automatikus).
 * @param {boolean} [props.largeButtons=false]               - Nagyméretű gomb stílus.
 * @param {'xs'|'sm'|'md'|'lg'|'xl'|false} [props.maxWidth='sm'] - MUI Dialog maxWidth.
 * @param {boolean} [props.disabled=false]                   - Teljes dialógus tiltása.
 * @returns {JSX.Element}
 */
const ConfirmDialogV2UI = ({
  open,
  onClose,
  title,
  message,
  buttons = [],
  largeButtons = false,
  maxWidth = 'sm',
  disabled = false,
}) => {
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (open) setLocked(false);
  }, [open, buttons]);

  const makeOnceHandler = (fn) => async (e) => {
    if (locked || disabled) return;
    setLocked(true);
    try {
      const ret = fn?.(e);
      if (ret && typeof ret.then === 'function') {
        await ret;
      }
    } catch (err) {
      setLocked(false);
      throw err;
    }
  };

  const handleClose = (e) => {
    setLocked(false);
    onClose?.(e);
  };

  // Normalize buttons, ensure "Mégse" is last
  const actionButtons = (Array.isArray(buttons) ? buttons : [])
    .filter(Boolean)
    .filter((b) => !b?.cancel && b?.label !== 'Mégse');

  const finalButtons = [
    ...actionButtons,
    {
      label: 'Mégse',
      variant: 'contained',
      color: '#6a838f',
      onClick: handleClose,
      cancel: true,
    },
  ];

  const sizeSx = largeButtons ? { height: 100, fontSize: '1.25rem' } : {};

  return (
    <DialogUI
      open={open}
      onClose={onClose}
      headerContent={title ?? 'Művelet megerősítése'}
      fullWidth={true}
      maxWidth={maxWidth}
    >
      <Typography variant={largeButtons ? 'h5' : 'h6'} sx={{ mt: 1, mb: 2 }} align="center">
        {message ?? 'Megerősíti a műveletet?'}
      </Typography>

      <Grid container spacing={2} justifyContent="center" alignItems="center">
        {finalButtons.map((btn, idx) => {
          const hex = isHexColor(btn.color) ? btn.color : null;
          const variant = btn.variant ?? 'contained';
          return (
            <Grid item key={idx} xs={12} sm={6} md={4} lg={3}>
              <Button
                fullWidth
                variant={variant}
                color={hex ? 'inherit' : btn.color ?? 'primary'}
                onClick={makeOnceHandler(btn.onClick ?? handleClose)}
                disabled={disabled || locked || btn.disabled}
                autoFocus={btn.autoFocus}
                sx={{
                  ...sizeSx,
                  ...(hex ? getHexColorSx(hex, variant) : {}),
                  color: variant === 'contained' && 'white',
                  fontWeight: 'bold',
                  fontSize: largeButtons ? '1.5rem' : 'inherit',
                }}
              >
                {btn.label}
              </Button>
            </Grid>
          );
        })}
      </Grid>
    </DialogUI>
  );
};

export default ConfirmDialogV2UI;
